import useUsers from "../../hooks/useUsers";
import { useNavigate } from "react-router-dom";
import useAllTransactions from "src/hooks/useAllTransactions";
import { ETransactionType } from "src/helpers/constants";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ImportReceivedTransaction from "../ImportReceivedTransaction/ImportReceivedTransaction";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { deleteTransactions, updateTransaction } from "src/api/transactions";
import { showNotification } from "src/helpers/utils";
import useCache from "src/hooks/useCache";
import moment from "moment";
import TransactionsTable from "src/pages/Transactions/TransactionsTable";
import Cards from "../Cards/Cards";
import { EButtonKind } from "../Button/ButtonTypes";

interface IReceivedTransactionsList {
  columns?: number;
}

const ReceivedTransactionsList: React.FC<IReceivedTransactionsList> = ({
  columns = 1,
}) => {
  const { clearCache } = useCache();
  const [isImportingTransaction, setIsImportingTransaction] =
    useState<boolean>(false);
  const { getUserById } = useUsers();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allReceivedTransactions } = useAllTransactions();
  const [showImportTransactionDialog, setShowImportTransactionDialog] =
    useState<boolean>(false);
  const [isDeletingTransaction, setIsDeletingTransaction] =
    useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [activeTransaction, setActiveTransaction] = useState<any>(null);

  const getUserData = (_id: string) => {
    return getUserById({ _id }) || {};
  };

  const deleteTransaction = async () => {
    let hasError = false;

    setIsDeletingTransaction(true);

    await deleteTransactions({
      transactionIds: [activeTransaction?.invoice],
      sendToRecycle: false,
    }).then(async (data) => {
      if (hasError) {
        showNotification({
          kind: "error",
          message: `Error eliminando la transacción compartida`,
        });
        setIsDeletingTransaction(false);
      } else {
        await clearCache();

        showNotification({
          message: "Transacción eliminada permanentemente",
          kind: "success",
        });
      }

      setIsDeletingTransaction(false);
      setShowDeleteDialog(false);
    });
  };

  const updateTransactionData = async ({
    account,
    type,
    payTo,
    date,
  }: {
    account: string;
    type: ETransactionType;
    payTo: string;
    date: string;
  }) => {
    setIsImportingTransaction(true);
    try {
      const result = await updateTransaction({
        transactionId: activeTransaction?.invoice || "",
        fieldsToUpdate: {
          account,
          shared: false,
          payTo,
          type,
          date: moment(date).format("yyyy-MM-DD"),
        },
      });

      if (result?.success) {
        await clearCache();

        setShowImportTransactionDialog(false);
        setIsImportingTransaction(false);

        showNotification({
          message: "Transacción importada correctamente",
          kind: "success",
        });
      } else {
        setIsImportingTransaction(false);
        showNotification({
          message: "Ocurrió un error al importar la transacción",
          kind: "error",
        });
      }
    } catch (e) {
      setIsImportingTransaction(false);
      showNotification({
        message: "Ocurrió un error al importar la transacción",
        kind: "error",
      });
    }
  };

  if (!allReceivedTransactions?.length) return null;

  return (
    <>
      <Cards>
        {allReceivedTransactions?.map?.((transaction: any, key: number) => (
          <Cards.ReceivedTransaction
            key={key}
            transaction={transaction}
            index={key}
          />
        ))}
      </Cards>

      {showImportTransactionDialog && (
        <ImportReceivedTransaction
          onClose={() => setShowImportTransactionDialog(false)}
          onConfirm={async ({ account, type, payTo, date }) => {
            await updateTransactionData({ account, type, payTo, date });
          }}
          transaction={activeTransaction}
          isLoading={isImportingTransaction}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          description="¿Deseas eliminar esta transacción compartida de forma permanente?"
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={async () => {
            await deleteTransaction();
          }}
          title="Eliminar"
          confirmKind={EButtonKind.DANGER}
          doubleConfirmRequired
          isLoading={isDeletingTransaction}
        />
      )}
    </>
  );
};

export default ReceivedTransactionsList;
